import React, { useState, useEffect } from "react";
import { useCart } from "../context/CartContext"; // adjust path
import { useToast } from "../context/ToastContext";

export default function ItemModal({ item, isOpen, onClose }) {
  const { addToCart, cartItems } = useCart();
  const { addToast } = useToast();
  const [quantity, setQuantity] = useState(1);
  const [selectedRequiredOption, setSelectedRequiredOption] = useState(null);
  const [selectedOptionalOptions, setSelectedOptionalOptions] = useState([]);

  // Reset modal state when item changes
  useEffect(() => {
    setQuantity(1);
    setSelectedRequiredOption(item?.required_options?.[0] || null);
    setSelectedOptionalOptions([]);
  }, [item]);

  // Close modal on Escape
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  if (!isOpen || !item) return null;

  // ------------------ Handlers ------------------
  const handleOptionalChange = (option) => {
    const exists = selectedOptionalOptions.find((o) => o.id === option.id);
    if (exists) {
      setSelectedOptionalOptions((prev) =>
        prev.filter((o) => o.id !== option.id),
      );
    } else {
      setSelectedOptionalOptions((prev) => [
        ...prev,
        { ...option, quantity: 1 },
      ]);
    }
  };

  const updateOptionalQuantity = (optionId, change) => {
    setSelectedOptionalOptions((prev) =>
      prev.map((opt) =>
        opt.id === optionId
          ? {
              ...opt,
              quantity: Math.max(1, (Number(opt.quantity) || 1) + change),
            }
          : opt,
      ),
    );
  };

  const increaseQuantity = () => setQuantity((q) => q + 1);
  const decreaseQuantity = () => setQuantity((q) => Math.max(1, q - 1));

  // ------------------ Total Price ------------------
  const totalPrice =
    quantity *
    (Number(item.price || 0) +
      Number(selectedRequiredOption?.option_price || 0) +
      selectedOptionalOptions.reduce(
        (sum, opt) => sum + Number(opt.option_price || 0) * (opt.quantity || 1),
        0,
      ));

  // ------------------ Add to Basket ------------------
  const handleAddToBasket = () => {
    if (item.required_options?.length > 0 && !selectedRequiredOption) {
      alert("Please select a required option.");
      return;
    }

    // Check if same item with same options exists
    const existingItem = cartItems.find((cartItem) => {
      if (cartItem.item.id !== item.id) return false;
      if (
        (cartItem.selectedRequiredOption?.id || null) !==
        (selectedRequiredOption?.id || null)
      )
        return false;
      if (
        cartItem.selectedOptionalOptions.length !==
        selectedOptionalOptions.length
      )
        return false;
      for (let opt of selectedOptionalOptions) {
        if (!cartItem.selectedOptionalOptions.find((o) => o.id === opt.id))
          return false;
      }
      return true;
    });

    if (existingItem) {
      // Increment quantity if already in cart
      const newQuantity = existingItem.quantity + quantity;
      existingItem.quantity = newQuantity;
      existingItem.totalPrice = newQuantity * (totalPrice / quantity);
    } else {
      
      addToCart({
        item: {
          ...item,
          item_name: item.name, 
        },
        quantity,
        selectedRequiredOption,
        selectedOptionalOptions,
      });
    }

    onClose();
    addToast("Item added to cart!");
  };

  // ------------------ Helper ------------------
  const formatMoney = (n) => `£${Number(n || 0).toFixed(2)}`;

  // ------------------ Render ------------------
  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <div
            className="bg-white rounded-3xl w-full max-w-lg max-h-[95vh] overflow-auto shadow-lg relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              className="absolute top-4 right-4 font-bold bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-md hover:bg-gray-200 hover:text-orange-500"
              onClick={onClose}
              aria-label="Close"
            >
              ✕
            </button>

            {/* Item Image */}
            {item.image ? (
              <img
                src={`https://deedelicious.com/${item.image}`}
                alt={item.name}
                className="w-full h-64 object-cover"
              />
            ) : (
              <div className="w-full h-64 bg-gray-200 flex items-center justify-center text-gray-500">
                No image
              </div>
            )}

            <div className="p-6 flex flex-col gap-4">
              {/* Name & Description */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {item.name}
                </h2>
                <p className="mt-2 text-gray-600 text-sm">{item.description}</p>
              </div>

              {/* Required Options */}
              {item.required_options?.length > 0 && (
                <div>
                  <p className="font-semibold mb-2">Required Option:</p>
                  <div className="flex gap-3 flex-wrap">
                    {item.required_options.map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setSelectedRequiredOption(opt)}
                        className={`px-4 py-2 rounded-xl border font-medium text-sm transition ${
                          selectedRequiredOption?.id === opt.id
                            ? "bg-orange-500 text-white border-orange-500"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-orange-50"
                        }`}
                      >
                        {opt.option_name}{" "}
                        {opt.option_price > 0
                          ? `(+${formatMoney(opt.option_price)})`
                          : ""}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Optional Options */}
              {item.optional_options?.length > 0 && (
                <div>
                  <p className="font-semibold mb-2">Optional Options:</p>
                  <div className="flex flex-col gap-2">
                    {item.optional_options.map((opt) => {
                      const selected = selectedOptionalOptions.find(
                        (o) => o.id === opt.id,
                      );
                      return (
                        <div key={opt.id} className="flex items-center gap-3">
                          <button
                            onClick={() => handleOptionalChange(opt)}
                            className={`px-4 py-2 rounded-xl border font-medium text-sm transition flex-1 text-left ${
                              selected
                                ? "bg-orange-500 text-white border-orange-500"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-orange-50"
                            }`}
                          >
                            {opt.option_name}{" "}
                            {opt.option_price > 0
                              ? `(+${formatMoney(opt.option_price)})`
                              : ""}
                          </button>

                          {selected && (
                            <div className="flex items-center gap-2 border rounded-xl px-3 py-1">
                              <button
                                onClick={() =>
                                  updateOptionalQuantity(opt.id, -1)
                                }
                                className="px-2 font-bold"
                              >
                                −
                              </button>
                              <span className="w-6 text-center">
                                {selected.quantity}
                              </span>
                              <button
                                onClick={() =>
                                  updateOptionalQuantity(opt.id, 1)
                                }
                                className="px-2 font-bold"
                              >
                                +
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quantity Selector */}
              <div className="flex items-center gap-4">
                <p className="font-semibold">Quantity:</p>
                <div className="flex items-center gap-2 border rounded-xl px-3 py-1">
                  <button
                    onClick={decreaseQuantity}
                    className="text-lg font-bold px-2"
                  >
                    −
                  </button>
                  <span className="w-6 text-center">{quantity}</span>
                  <button
                    onClick={increaseQuantity}
                    className="text-lg font-bold px-2"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Add to Basket */}
              <button
                onClick={handleAddToBasket}
                className="mt-6 w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-2xl font-semibold text-lg transition flex justify-center gap-2 items-center px-6"
              >
                <span>Add to Basket</span>
                <span className="ml-4">{formatMoney(totalPrice)}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
